// How the account-closure dialog should ask for confirmation. The API decides
// what a login can offer; this turns that answer into the one thing the dialog
// shows, so the component has no branching rules of its own.

// The API's answer about the signed-in login, as the page data carries it.
export interface AccountConfirmation {
  // Whether the login has a password of its own to type.
  readonly has_password: boolean;
  // Whether the current session was established recently enough to stand in for
  // a password. Only meaningful for a login that has none.
  readonly signed_in_recently: boolean;
  // Whether this login reaches the administrator console, so the account menu
  // can offer the link. Optional, because an older API answer carries no such
  // field and the absence has to read as "no"; it grants nothing either way —
  // the console's own guard decides, and a browser that forges it reaches a 404.
  readonly is_admin?: boolean;
}

export type DeleteDialogMode =
  // Ask for the account password.
  | "password"
  // No password exists; send the visitor to sign in again first.
  | "reauthenticate"
  // No password exists, but the visitor has just signed in again.
  | "reauthenticated";

// A login with a password always types it — a recent sign-in never stands in
// for one. Without an answer from the API (it was unreachable while the page
// rendered) the dialog asks for a password, the case that fits almost every
// account; the API re-checks regardless, so a wrong guess here costs a refused
// attempt and never a wrong deletion.
export function deleteDialogMode(confirmation: AccountConfirmation | null): DeleteDialogMode {
  if (confirmation === null || confirmation.has_password) return "password";
  return confirmation.signed_in_recently ? "reauthenticated" : "reauthenticate";
}
